<script setup lang="ts">
import { ref, onMounted, onUnmounted, inject, watch } from "vue";
import { EventBus } from "~/code/event-bus";
import MasterLayout from "~/layouts/master.vue";
import { useToastLogStore } from "@/store/toast-log";

const showDrawer = ref(false);
const toastDrawerWidth = ref(360);
let resizeStartX = 0;
let resizeStartWidth = 0;
const resizeToastLog = (event: PointerEvent) => {
  toastDrawerWidth.value = Math.max(280, Math.min(window.innerWidth * 0.9, resizeStartWidth + resizeStartX - event.clientX));
};
const stopToastResize = () => {
  document.removeEventListener("pointermove", resizeToastLog);
  document.removeEventListener("pointerup", stopToastResize);
  document.removeEventListener("pointercancel", stopToastResize);
};
const startToastResize = (event: PointerEvent) => {
  resizeStartX = event.clientX;
  resizeStartWidth = toastDrawerWidth.value;
  document.addEventListener("pointermove", resizeToastLog);
  document.addEventListener("pointerup", stopToastResize);
  document.addEventListener("pointercancel", stopToastResize);
};
const resizeToastWithKeyboard = (event: KeyboardEvent) => {
  if (!["ArrowLeft", "ArrowRight"].includes(event.key)) return;
  event.preventDefault();
  toastDrawerWidth.value = Math.max(280, Math.min(window.innerWidth * 0.9, toastDrawerWidth.value + (event.key === "ArrowLeft" ? 20 : -20)));
};

const toast = useToastLogStore();
const toastLog = toast.logs;

const toggleDrawerHandler = () => {
  showDrawer.value = !showDrawer.value;
};
onMounted(() => {
  EventBus.on("toggleNotificationsDrawer", toggleDrawerHandler);
});

onUnmounted(() => {
  stopToastResize();
  EventBus.off("toggleNotificationsDrawer", toggleDrawerHandler);
});
</script>

<template>
  <MasterLayout>
    <v-locale-provider>
      <v-app>
        <v-navigation-drawer v-if="showDrawer" location="right" :width="toastDrawerWidth" class="toast-log-drawer">
          <div class="toast-log-resizer" role="separator" aria-label="Resize toast log" aria-orientation="vertical"
            :aria-valuenow="toastDrawerWidth" tabindex="0"
            @pointerdown.prevent="startToastResize" @keydown="resizeToastWithKeyboard" />
          <div style="padding: 30px">
            <h3>Toast Log</h3>

            <div
              v-for="(toast, index) in toastLog.slice().reverse()"
              :key="index"
            >
              <div :class="`toast toast-${toast.type}`">
                {{ toast.message }}
              </div>
            </div>
          </div>
        </v-navigation-drawer>
        <LayoutFullMain />
        <v-main>
          <v-container fluid class="page-wrapper">
            <div class="maxWidth">
              <NuxtPage />
            </div>
          </v-container>
        </v-main>
      </v-app>
    </v-locale-provider>
  </MasterLayout>
</template>

<style>
.toast-log-drawer .v-navigation-drawer__content { position: relative; }
.toast-log-resizer {
  position: sticky;
  top: 0;
  left: 0;
  width: 8px;
  height: 100%;
  float: left;
  cursor: ew-resize;
  touch-action: none;
  background: rgba(var(--v-theme-primary), 0.15);
}
.toast-log-resizer:hover, .toast-log-resizer:focus-visible { background: rgba(var(--v-theme-primary), 0.5); }
.toast-log-drawer .toast { overflow-wrap: anywhere; }
.form-text {
  height: 56px;
  display: flex;
  align-items: center;
  padding: 0 18px;
}

.filepond--credits {
  display: none;
}
.drag-handle {
  padding: 8px;
  padding-bottom: 8px;
  cursor: grab;
  text-align: left;
  background-color: rgba(var(--v-theme-primary), 1);
  border-radius: 5px;
  width: 100px;
}
.drag-handle:active {
  cursor: grabbing;
  background-color: rgba(var(--v-theme-primary-darken-1), 1);
}
.v-chip svg {
  padding-right: 8px;
}
.v-chip {
  margin: 4px 8px 4px 0;
}
.v-data-table tbody tr:nth-child(even) {
  background-color: rgb(157 0 255 / 0%);
}

.v-data-table tbody tr:nth-child(odd) {
  background-color: rgb(157 0 255 / 5%);
}

.v-data-table .v-table__wrapper > table > thead > tr > td,
.v-data-table .v-table__wrapper > table > thead > tr th,
.v-data-table .v-table__wrapper > table tbody > tr > td,
.v-data-table .v-table__wrapper > table tbody > tr th {
  background-color: transparent;
}

.close-btn {
  cursor: pointer;
  margin-left: 8px;
}

.toast {
  margin: 20px 0;
  font-weight: bold;
}
.toast-success {
  color: var(--toastify-color-success);
}

.toast-error {
  color: var(--toastify-color-error);
}

.toast-info {
  color: var(--toastify-color-info);
}

.datetimepicker.is-dark .datepicker[data-v-e6a80f26],
.datetimepicker.is-dark,
.field.is-dark {
  background: #232024;
  border: 0;
}
.dialog-overflow .v-overlay__content {
  overflow: visible !important;
}
</style>
